import { SortingDirection } from "enums";
import {
  type FilterDefinition,
  type TableQueryHandlers,
  type TableQueryParamsResult,
  type TableQueryState,
  useTableQueryParams,
} from "hooks/useTableQueryParams";

import { Columns, ColumnToParamMap } from "../getColumnOptions";
import { SurveyFiltersEnum } from "../../types/models";

const DEFAULT_PAGE_SIZE = 10;

const DATE_CREATED_FROM_PARAM = `${SurveyFiltersEnum.DateCreated}From`;
const DATE_CREATED_TO_PARAM = `${SurveyFiltersEnum.DateCreated}To`;
const DATE_MODIFIED_FROM_PARAM = `${SurveyFiltersEnum.DateModified}From`;
const DATE_MODIFIED_TO_PARAM = `${SurveyFiltersEnum.DateModified}To`;

const parseNumberArray = (value: string): number | undefined => {
  if (!value) {
    return undefined;
  }

  const parsed = Number.parseInt(value, 10);

  return Number.isFinite(parsed) ? parsed : undefined;
};

// Maps API param -> display column name
const paramToColumn = Object.entries(ColumnToParamMap).reduce<Record<string, string>>((acc, [column, param]) => {
  // Column keys are lowercase, capitalize first letter for display
  acc[param] = column.charAt(0).toUpperCase() + column.slice(1);
  return acc;
}, {});

const filterDefinitions: ReadonlyArray<FilterDefinition<SurveyFiltersEnum>> = [
  {
    key: SurveyFiltersEnum.Publishers,
    type: "array",
    parse: parseNumberArray,
  },
  {
    key: SurveyFiltersEnum.Tags,
    type: "array",
  },
  {
    key: SurveyFiltersEnum.SoftwareApplications,
    type: "array",
  },
  {
    key: SurveyFiltersEnum.Labels,
    type: "array",
  },
  {
    key: SurveyFiltersEnum.Packs,
    type: "array",
    parse: parseNumberArray,
  },
  {
    key: SurveyFiltersEnum.DateCreated,
    type: "range",
    fromParam: DATE_CREATED_FROM_PARAM,
    toParam: DATE_CREATED_TO_PARAM,
  },
  {
    key: SurveyFiltersEnum.DateModified,
    type: "range",
    fromParam: DATE_MODIFIED_FROM_PARAM,
    toParam: DATE_MODIFIED_TO_PARAM,
  },
];

// Valid sort keys (API parameter names)
const sortingKeys = Object.values(ColumnToParamMap);

// Default sorting: Added (dateCreated) descending
const defaultSortBy = ColumnToParamMap[Columns.Added.toLowerCase() as keyof typeof ColumnToParamMap];
const defaultSortDirection = SortingDirection.Descending;

const surveysQueryConfig = {
  defaultPageSize: DEFAULT_PAGE_SIZE,
  filters: filterDefinitions,
  sorting: {
    valid: sortingKeys,
    defaultSortBy,
    defaultDirection: defaultSortDirection,
    sortKeyToColumnName: paramToColumn,
    fallbackColumnName: Columns.Added,
  },
} as const;

export type SurveysQueryState = TableQueryState<SurveyFiltersEnum>;
export type SurveysQueryHandlers = TableQueryHandlers<SurveyFiltersEnum>;
export type SurveysQueryParamsResult = TableQueryParamsResult<SurveyFiltersEnum>;

export const useSurveysQueryParams = (): SurveysQueryParamsResult => {
  return useTableQueryParams<SurveyFiltersEnum>(surveysQueryConfig);
};

export default useSurveysQueryParams;
